'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useIsImpersonating } from '@/lib/next/authentication/impersonating.client'
import { Avatar } from '@/ui/avatar/Avatar'
import { Card } from '@/ui/cards/card/Card'
import { LoadingOverlay } from '@/ui/loaders/loading-overlay/LoadingOverlay'
import { useToast } from '@/ui/toast/Toast'
import { gql } from '@gql-generated/gql'
import type { SelectCompanyCard_UserFragment } from '@gql-generated/graphql'
import { useTransition } from 'react'
import { useHover } from 'react-aria'
import type { SelectUserActionInput } from '../../services/account.action'

gql(/* GraphQL */ `
  fragment SelectCompanyCard_User on User {
    id
    email
    favoritePointOfSale {
      id
      name
    }
    company {
      id
      code
      name
      pointOfSale {
        id
        name
        subsidiary {
          id
        }
        address {
          line1
        }
      }
    }
  }
`)

type SelectCompanyCardProps = {
  account: SelectCompanyCard_UserFragment
  isCurrentCompany?: boolean
  onSelect?: (account: SelectUserActionInput) => Promise<void>
  isLoading?: boolean
}

/**
 *
 * Used by account drawer (from marketing header)
 */
export function SelectCompanyCard({
  account,
  isCurrentCompany = false,
  onSelect,
  isLoading,
}: SelectCompanyCardProps) {
  const { t } = useTranslation(['generic', 'company'])
  const { company } = account
  const [isSubmitting, startTransition] = useTransition()
  const toast = useToast()
  const isImpersonating = useIsImpersonating()
  const { isHovered, hoverProps } = useHover({ isDisabled: isImpersonating })

  if (!company?.code || !company?.pointOfSale) return null

  async function handleSubmit() {
    startTransition(async () => {
      if (company && company.pointOfSale && company?.code) {
        try {
          await onSelect?.({
            accountId: account.id,
            company: {
              companyCode: company.code,
              pointOfSaleId: company.pointOfSale.id,
              subsidiaryId: company.pointOfSale.subsidiary.id,
              favoritePointOfSaleId: account.favoritePointOfSale?.id ?? '',
            },
          })
        } catch {
          toast.error({
            title: t('generic:error'),
          })
        }
      }
    })
  }

  return (
    <form action={handleSubmit}>
      <input type="hidden" name="code" value={company.code} />
      <button
        data-testid="company-list/company"
        className={twMerge(
          'relative w-full text-left',
          isLoading && 'cursor-not-allowed',
          isImpersonating && 'cursor-default',
          !isImpersonating && !isLoading && !isSubmitting && 'group/avatar',
        )}
        type="submit"
        disabled={isCurrentCompany || isLoading || isSubmitting || isImpersonating}
        {...hoverProps}
      >
        <Card
          padding="sm"
          skin="body-alt"
          className={twMerge(
            'h-10 border-1.5 border-clear',
            !isImpersonating &&
              !isCurrentCompany &&
              !isLoading &&
              !isSubmitting &&
              isHovered &&
              'border-brand-2-500',
            isImpersonating && !isCurrentCompany && 'opacity-40',
          )}
        >
          <LoadingOverlay isVisible={isSubmitting} />
          <div className="flex gap-2 px-4">
            <div className="flex flex-col items-center justify-center">
              <Avatar active={isCurrentCompany} name={company.name || company.pointOfSale.name} />
            </div>
            <div className="flex flex-col justify-center font-medium leading-light">
              {company?.name && <div className="line-clamp-1 font-bold">{company.name}</div>}
              {company?.code && (
                <div className="line-clamp-1">
                  {t('company:code.short', { code: company.code })}
                </div>
              )}
              <div className="line-clamp-1">
                {account.favoritePointOfSale
                  ? account.favoritePointOfSale.name
                  : company.pointOfSale.name}
              </div>
            </div>
          </div>
        </Card>
      </button>
    </form>
  )
}
